import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Share, PlusSquare, X } from "lucide-react";
import { canPromptInstall, isAndroid, isInstalledHere, isIOS, isIOSSafari, isPhone, isStandalone, onInstallChange, promptInstall } from "@/lib/pwaInstall";

const WEEK = 7 * 24 * 3600 * 1000;
const SETUP_PATHS = ["/login", "/register", "/signup", "/accept-invite", "/invite", "/reset-password", "/forgot-password", "/mfa", "/security", "/onboarding", "/set-password", "/install-help"];

function useInstallAvailable() {
  const [, force] = useState(0);
  useEffect(() => onInstallChange(() => force((n) => n + 1)), []);
  return canPromptInstall();
}

export function InstallSteps() {
  const native = useInstallAvailable();
  if (isStandalone()) return <p className="text-sm text-slate-600" data-testid="install-already">You're already using the installed Top-Recruit app.</p>;
  if (isIOS() && !isIOSSafari()) return (
    <div className="text-sm text-slate-700 space-y-2" data-testid="install-ios-other-browser">
      <p>On iPhone and iPad, Top-Recruit can only be added to the Home Screen from <b>Safari</b>.</p>
      <ol className="list-decimal pl-5 space-y-1"><li>Copy this page's address (or tap your browser's menu → “Open in Safari”).</li><li>Open the <b>Safari</b> app (the blue compass icon) and paste the address.</li><li>Sign in, then follow the Safari steps below.</li></ol>
    </div>);
  if (isIOS()) return (
    <ol className="text-sm text-slate-700 list-decimal pl-5 space-y-1.5" data-testid="install-ios-steps">
      <li>Tap the <b>Share</b> button <Share className="w-4 h-4 inline -mt-1" /> in Safari's toolbar.</li>
      <li>Scroll down and tap <b>Add to Home Screen</b> <PlusSquare className="w-4 h-4 inline -mt-1" />.</li>
      <li>Tap <b>Add</b>. Open Top-Recruit from its Home Screen icon — you stay signed in with the same account.</li>
    </ol>);
  if (native) return <p className="text-sm text-slate-700" data-testid="install-native-available">Tap <b>Install App</b> to add Top-Recruit to your device.</p>;
  return (
    <div className="text-sm text-slate-700 space-y-2" data-testid="install-manual-steps">
      <p>{isAndroid() ? "Your browser didn't offer one-tap install here. In Chrome:" : "In Chrome or Edge:"}</p>
      <ol className="list-decimal pl-5 space-y-1"><li>Open the browser menu (⋮).</li><li>Tap <b>Install app</b> or <b>Add to Home screen</b>.</li><li>Confirm. Some browsers (for example Firefox or in-app browsers) only support adding a shortcut, or none — open Top-Recruit in Chrome if the option is missing.</li></ol>
    </div>);
}

export function InstallAppDialog({ open, onClose }) {
  const native = useInstallAvailable();
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-sm" data-testid="install-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="flex items-center gap-2"><img src="/icons/icon-192.png" alt="" className="w-8 h-8 rounded-lg" />Install Top-Recruit</DialogTitle></DialogHeader>
        <InstallSteps />
        {native && !isStandalone() && <Button onClick={async () => { await promptInstall(); onClose(); }} className="bg-brand hover:bg-brand-strong text-white" data-testid="install-dialog-native">Install App</Button>}
        <p className="text-[11px] text-slate-400">Installing adds an app icon only. It uses your existing account and the normal sign-in and security checks.</p>
      </DialogContent>
    </Dialog>
  );
}

export function InstallPrompt() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const native = useInstallAvailable();
  const [show, setShow] = useState(false);
  const [steps, setSteps] = useState(false);
  const key = user ? `tr_install_prompt_${user.id}` : null;
  useEffect(() => {
    if (!user || !isPhone() || isInstalledHere() || SETUP_PATHS.some((p) => pathname.startsWith(p)) || user.mfa_setup_required) return;
    if (sessionStorage.getItem("tr_install_prompt_seen")) return;
    const last = Number(localStorage.getItem(key) || 0);
    if (Date.now() - last < WEEK) return;
    let alive = true;
    api.get("/my-profile/install-prompt").then((r) => {
      if (!alive || r.data.dont_show) return;
      sessionStorage.setItem("tr_install_prompt_seen", "1");
      localStorage.setItem(key, String(Date.now()));
      setShow(true);
    }).catch(() => {});
    return () => { alive = false; };
  }, [user, pathname, key]);
  if (!show) return null;
  const later = () => { localStorage.setItem(key, String(Date.now())); setShow(false); };
  const never = () => { api.put("/my-profile/install-prompt", { dont_show: true }).catch(() => {}); setShow(false); };
  const install = async () => {
    if (native) { const o = await promptInstall(); if (o === "accepted") setShow(false); return; }
    setSteps(true);
  };
  return (
    <div role="region" aria-label="Install Top-Recruit" data-testid="install-prompt"
      className={`fixed z-40 left-3 right-3 ${pathname.startsWith("/portal") ? "bottom-20" : "bottom-3"} sm:left-auto sm:right-4 sm:w-96 rounded-xl border border-slate-200 bg-white shadow-lg p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] tr-animate-in`}>
      <div className="flex items-start gap-3">
        <img src="/icons/icon-192.png" alt="Top-Recruit" className="w-10 h-10 rounded-lg shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Install Top-Recruit</p>
          <p className="text-xs text-slate-500">Open Top-Recruit from your Home Screen, with the same account.</p>
        </div>
        <button onClick={later} aria-label="Remind me later" className="p-1.5 -m-1 text-slate-400 hover:text-slate-600" data-testid="install-prompt-close"><X className="w-4 h-4" /></button>
      </div>
      {steps && <div className="mt-2 rounded-lg bg-slate-50 p-2.5"><InstallSteps /></div>}
      <div className="mt-2.5 flex flex-wrap gap-2">
        {!steps && <Button size="sm" onClick={install} className="h-9 bg-brand hover:bg-brand-strong text-white" data-testid="install-prompt-install">{native ? "Install App" : "Show me how"}</Button>}
        <Button size="sm" variant="outline" onClick={later} className="h-9" data-testid="install-prompt-later">Remind Me Later</Button>
        <Button size="sm" variant="ghost" onClick={never} className="h-9 text-slate-500" data-testid="install-prompt-never">Don't Show Again</Button>
      </div>
    </div>
  );
}
